import React, { useCallback, useMemo, useState } from "react";
import {
  useList,
  useNotification,
  useOne,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import {
  Alert,
  Badge,
  Button,
  Empty,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { LinkOutlined, DisconnectOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { AccountType } from "../../models/account";

type PricingPlanAccountsModalProps = {
  open: boolean;
  planId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

type LinkedAccount = {
  id: number;
  name: string;
  type: string;
};

type AccountOption = {
  id: number;
  name: string;
  type: string;
};

const EMPTY_ACCOUNTS: LinkedAccount[] = [];

export const PricingPlanAccountsModal: React.FC<PricingPlanAccountsModalProps> = ({
  open,
  planId,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const { open: openNotification } = useNotification();
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [isLinking, setIsLinking] = useState(false);
  const [unlinkingAccountId, setUnlinkingAccountId] = useState<number | null>(null);

  const {
    data: planData,
    isLoading: planLoading,
    refetch: refetchPlan,
  } = useOne({
    resource: "pricing-plans",
    id: planId ?? "",
    queryOptions: {
      enabled: open && !!planId,
    },
  });

  const { data: accountsListData, isLoading: accountsLoading } = useList({
    resource: "accounts",
    pagination: { mode: "off" },
    queryOptions: {
      enabled: open,
    },
  });

  const { mutateAsync: updateAccount } = useUpdate();

  const record = planData?.data;
  const linkedAccounts: LinkedAccount[] = useMemo(() => {
    const accounts = record?.accounts;
    if (!Array.isArray(accounts)) return EMPTY_ACCOUNTS;
    return accounts
      .map((account: { id?: number | string; name?: string; type?: string }) => ({
        id: Number(account.id),
        name: typeof account.name === "string" ? account.name : "",
        type: typeof account.type === "string" ? account.type : "",
      }))
      .filter((account) => Number.isFinite(account.id));
  }, [record?.accounts]);

  const linkedAccountIds = useMemo(
    () => new Set(linkedAccounts.map((account) => account.id)),
    [linkedAccounts],
  );

  const availableAccountOptions = useMemo(() => {
    const allAccounts = accountsListData?.data ?? [];
    return allAccounts
      .map((account: { id?: number | string; name?: string; type?: string }) => ({
        id: Number(account.id),
        name: typeof account.name === "string" ? account.name : "",
        type: typeof account.type === "string" ? account.type : "",
      }))
      .filter(
        (account): account is AccountOption =>
          Number.isFinite(account.id) && !linkedAccountIds.has(account.id),
      )
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [accountsListData?.data, linkedAccountIds]);

  const selectOptions = useMemo(
    () =>
      availableAccountOptions.map((account) => ({
        value: account.id,
        label: account.name,
        name: account.name,
        type: account.type,
      })),
    [availableAccountOptions],
  );

  const loading = planLoading || accountsLoading;
  const frozen = isLinking || unlinkingAccountId !== null;

  const handleClose = useCallback(() => {
    if (frozen) return;
    setSelectedAccountIds([]);
    onCancel();
  }, [frozen, onCancel]);

  const handleLinkAccounts = useCallback(async () => {
    if (!planId || selectedAccountIds.length === 0 || isLinking) return;
    setIsLinking(true);
    try {
      await Promise.all(
        selectedAccountIds.map((accountId) =>
          updateAccount({
            resource: "accounts",
            id: accountId,
            values: { pricingPlanId: Number(planId) },
          }),
        ),
      );
      openNotification?.({
        type: "success",
        message: _t("pricing-plans.accounts.linkSuccess"),
      });
      setSelectedAccountIds([]);
      await refetchPlan();
      onSuccess?.();
    } catch {
      openNotification?.({
        type: "error",
        message: _t("pricing-plans.accounts.linkError"),
      });
    } finally {
      setIsLinking(false);
    }
  }, [
    _t,
    isLinking,
    onSuccess,
    openNotification,
    planId,
    refetchPlan,
    selectedAccountIds,
    updateAccount,
  ]);

  const handleUnlinkAccount = useCallback(
    async (accountId: number) => {
      if (unlinkingAccountId !== null) return;
      setUnlinkingAccountId(accountId);
      try {
        await updateAccount({
          resource: "accounts",
          id: accountId,
          values: { pricingPlanId: null },
        });
        openNotification?.({
          type: "success",
          message: _t("pricing-plans.accounts.unlinkSuccess"),
        });
        await refetchPlan();
        onSuccess?.();
      } catch {
        openNotification?.({
          type: "error",
          message: _t("pricing-plans.accounts.unlinkError"),
        });
      } finally {
        setUnlinkingAccountId(null);
      }
    },
    [_t, onSuccess, openNotification, refetchPlan, unlinkingAccountId, updateAccount],
  );

  const accountColumns = useMemo(
    () => [
      {
        title: _t("accounts.fields.name"),
        dataIndex: "name",
        key: "name",
        render: (text: string, row: LinkedAccount) => (
          <Link to={`/accounts/show/${row.id}`}>{text}</Link>
        ),
      },
      {
        title: _t("accounts.fields.type"),
        dataIndex: "type",
        key: "type",
        render: (type: string) => (
          <Tag color={type === AccountType.company ? "blue" : "green"}>
            {_t(`accounts.fields.typeValues.${type}`)}
          </Tag>
        ),
      },
      {
        title: _t("table.actions"),
        key: "actions",
        width: 120,
        render: (_: unknown, row: LinkedAccount) => (
          <Popconfirm
            title={_t("pricing-plans.accounts.unlinkConfirm")}
            okText={_t("titles.yes")}
            cancelText={_t("buttons.cancel")}
            disabled={frozen}
            onConfirm={() => void handleUnlinkAccount(row.id)}
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DisconnectOutlined />}
              loading={unlinkingAccountId === row.id}
              disabled={frozen && unlinkingAccountId !== row.id}
            >
              {_t("pricing-plans.accounts.unlink")}
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [_t, frozen, handleUnlinkAccount, unlinkingAccountId],
  );

  return (
    <Modal
      title={_t("pricing-plans.titles.manageAccounts")}
      open={open}
      onCancel={handleClose}
      width={720}
      destroyOnClose
      maskClosable={!frozen}
      keyboard={!frozen}
      closable={!frozen}
      footer={
        <Button onClick={handleClose} disabled={frozen}>
          {_t("buttons.close")}
        </Button>
      }
    >
      <Spin spinning={loading}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Typography.Text type="secondary" className="text-sm">
            {record?.name}
          </Typography.Text>
          <Badge
            count={linkedAccounts.length}
            showZero
            color="orange"
            overflowCount={9999}
          />
        </div>

        <div className="mb-6 rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
          <Typography.Text strong className="mb-3 block">
            {_t("pricing-plans.accounts.linkSection")}
          </Typography.Text>
          <Alert
            type="info"
            showIcon
            className="mb-3"
            message={_t("pricing-plans.accounts.onePlanWarning")}
          />
          <Space direction="vertical" className="w-full" size="middle">
            <Select
              mode="multiple"
              showSearch
              allowClear
              className="w-full"
              placeholder={_t("pricing-plans.accounts.linkPlaceholder")}
              value={selectedAccountIds}
              options={selectOptions}
              disabled={frozen || availableAccountOptions.length === 0}
              filterOption={(input, option) => {
                const needle = input.trim().toLowerCase();
                if (!needle) return true;
                const opt = option as {
                  label?: string;
                  value?: number;
                  name?: string;
                  type?: string;
                };
                return (
                  (opt?.label ?? "").toLowerCase().includes(needle) ||
                  (opt?.name ?? "").toLowerCase().includes(needle) ||
                  (opt?.type ?? "").toLowerCase().includes(needle) ||
                  String(opt?.value ?? "").includes(needle)
                );
              }}
              optionRender={(option) => {
                const data = option.data as { value: number; name: string; type: string };
                return (
                  <span>
                    <span className="font-mono text-neutral-400">#{data.value}</span>{" "}
                    {data.name}
                    {data.type ? (
                      <Tag
                        className="ml-2"
                        color={data.type === AccountType.company ? "blue" : "green"}
                      >
                        {_t(`accounts.fields.typeValues.${data.type}`)}
                      </Tag>
                    ) : null}
                  </span>
                );
              }}
              onChange={(values) => setSelectedAccountIds(values as number[])}
            />
            <Button
              type="primary"
              icon={<LinkOutlined />}
              loading={isLinking}
              disabled={
                frozen || selectedAccountIds.length === 0 || availableAccountOptions.length === 0
              }
              onClick={() => void handleLinkAccounts()}
            >
              {_t("pricing-plans.accounts.linkButton")}
            </Button>
          </Space>
        </div>

        <Typography.Text strong className="mb-3 block">
          {_t("pricing-plans.fields.accounts")}
        </Typography.Text>
        {!loading && linkedAccounts.length === 0 ? (
          <Empty className="py-6" description={_t("pricing-plans.titles.noAccounts")} />
        ) : (
          <Table
            columns={accountColumns}
            dataSource={linkedAccounts}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Spin>
    </Modal>
  );
};
